import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { Table, Badge, Button, ButtonGroup } from "react-bootstrap";
import {
  FaEdit,
  FaTrash,
  FaEye,
  FaUsers,
  FaBuilding,
  FaGlobe,
  FaMapMarkerAlt,
  FaIndustry,
  FaCircle,
} from "react-icons/fa";
import {
  getCompany,
  deleteCompany,
} from "../../../features/companies/companiesSlice";
import { Company } from "../../../features/companies/companiesService";
import CompanyFormModal from "./CompanyFormModal";
import CompanyDetailModal from "./CompanyDetailModal";
import CompanyUsersModal from "./CompanyUsersModal";
import ConfirmationModal from "../../../components/common/ConfirmationModal";
import { AppDispatch } from "../../../store";
import { formatDate } from "../../../utils/dateUtils";
import { Icon } from "../../../components/common/Icon";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../context/ThemeContext";
import ClientCompaniesModal from "./CompanyClientCompaniesModal";

interface CompaniesListProps {
  companies: Company[];
}

const CompaniesList: React.FC<CompaniesListProps> = ({ companies }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();
  const { theme } = useTheme();
  const isDark = theme === "dark";

  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showUsersModal, setShowUsersModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [showClientCompaniesModal, setShowClientCompaniesModal] =
    useState(false);
  // Handler fonksiyonu ekleyin (diğer handler fonksiyonlarının yanına)
  const handleViewClientCompanies = (company: Company) => {
    setSelectedCompany(company);
    setShowClientCompaniesModal(true);
  };

  // Modal'ı kapatma fonksiyonu ekleyin (diğer close fonksiyonlarının yanına)
  const handleCloseClientCompaniesModal = () => {
    console.log("handleCloseClientCompaniesModal called");
    setShowClientCompaniesModal(false);
    // selectedCompany'yi null yapma - modal kendi state'ini yönetecek
  };
  // İşlemleri yönet
  const handleViewDetails = (company: Company) => {
    setSelectedCompany(company);
    setShowDetailModal(true);
  };

  const handleEdit = async (company: Company, e?: React.MouseEvent) => {
    try {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }

      setSelectedCompany(company);
      setShowEditModal(true);

      dispatch(getCompany(company.id)).catch((error) => {
        console.error("Error fetching company details:", error);
      });
    } catch (error) {
      console.error("Error handling edit:", error);
    }
  };

  const handleViewUsers = (company: Company) => {
    setSelectedCompany(company);
    setShowUsersModal(true);
  };

  const handleDeleteClick = (company: Company) => {
    setSelectedCompany(company);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    if (selectedCompany) {
      dispatch(deleteCompany(selectedCompany.id));
    }
    setShowDeleteModal(false);
    setSelectedCompany(null);
  };

  // Modal'ları kapat
  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setSelectedCompany(null);
  };

  const handleCloseDetailModal = () => {
    setShowDetailModal(false);
    setSelectedCompany(null);
  };

  const handleCloseUsersModal = () => {
    setShowUsersModal(false);
    setSelectedCompany(null);
  };

  // Şirket tipine göre badge
  const getCompanyTypeBadge = (type: string) => {
    switch (type.toLowerCase()) {
      case "agency":
        return (
          <span className="type-badge type-agency">
            <Icon icon={FaBuilding} size={12} />
            {t("agency")}
          </span>
        );
      case "client":
        return (
          <span className="type-badge type-client">
            <Icon icon={FaUsers} size={12} />
            {t("client")}
          </span>
        );
      default:
        return <span className="type-badge type-default">{t(type)}</span>;
    }
  };

  return (
    <>
      <div className="companies-table-wrapper">
        <Table className="companies-table" hover>
          <thead>
            <tr>
              <th className="table-header-cell">
                <span className="header-text">{t("company")}</span>
              </th>
              <th className="table-header-cell">
                <span className="header-text">{t("company_type")}</span>
              </th>
              <th className="table-header-cell">
                <span className="header-text">{t("industry")}</span>
              </th>
              <th className="table-header-cell">
                <span className="header-text">{t("location")}</span>
              </th>
              <th className="table-header-cell">
                <span className="header-text">{t("status")}</span>
              </th>
              <th className="table-header-cell">
                <span className="header-text">{t("created_at")}</span>
              </th>
              <th className="table-header-cell text-end">
                <span className="header-text">{t("actions")}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {companies.map((company, index) => (
              <tr
                key={company.id}
                className="table-row"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setSelectedCompany(company);
                  setShowEditModal(true);
                }}
                style={{ cursor: "pointer" }}
              >
                {/* Company */}
                <td className="table-cell">
                  <div className="company-info">
                    <div className="company-name">{company.company_name}</div>
                    <div className="company-meta">
                      <span className="company-id">
                        #{company.id.toString().padStart(4, "0")}
                      </span>
                      {company.website && (
                        <>
                          <span className="separator">•</span>
                          <Icon
                            icon={FaGlobe}
                            size={12}
                            className="website-icon"
                          />
                          <span className="website-text">{t("website")}</span>
                        </>
                      )}
                    </div>
                  </div>
                </td>

                {/* Company Type */}
                <td className="table-cell">
                  {getCompanyTypeBadge(company.company_type)}
                </td>

                {/* Industry */}
                <td className="table-cell">
                  <div className="industry-cell">
                    <Icon
                      icon={FaIndustry}
                      size={14}
                      className="industry-icon"
                    />
                    <span className="industry-text">
                      {company.industry || "-"}
                    </span>
                  </div>
                </td>

                {/* Location */}
                <td className="table-cell">
                  <div className="location-cell">
                    <Icon
                      icon={FaMapMarkerAlt}
                      size={14}
                      className="location-icon"
                    />
                    <span className="location-text">
                      {company.city && company.country
                        ? `${company.city}, ${company.country}`
                        : company.city || company.country || "-"}
                    </span>
                  </div>
                </td>

                {/* Status */}
                <td className="table-cell">
                  <div
                    className={`status-badge ${company.is_active ? "status-active" : "status-inactive"}`}
                  >
                    <Icon icon={FaCircle} size={8} className="status-dot" />
                    <span className="status-text">
                      {company.is_active ? t("active") : t("inactive")}
                    </span>
                  </div>
                </td>

                {/* Created At */}
                <td className="table-cell">
                  <span className="date-text">
                    {formatDate(company.created_at)}
                  </span>
                </td>
                {/* Actions */}
                <td className="table-cell text-end">
                  <div className="action-buttons">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="action-btn edit-btn"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setSelectedCompany(company);
                        setShowEditModal(true);
                      }}
                      title={t("edit")}
                    >
                      <Icon icon={FaEdit} size={14} />
                    </Button>

                    {/* Client Companies butonu - Sadece agency tipi şirketlerde göster */}
                    {company.company_type.toLowerCase() === "agency" && (
                      <Button
                        variant="outline-info"
                        size="sm"
                        className="action-btn client-companies-btn"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setSelectedCompany(company);
                          setShowClientCompaniesModal(true);
                        }}
                        title={t("client_companies")}
                      >
                        <Icon icon={FaBuilding} size={14} />
                      </Button>
                    )}

                    <Button
                      variant="outline-danger"
                      size="sm"
                      className="action-btn delete-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteClick(company);
                      }}
                      title={t("delete")}
                    >
                      <Icon icon={FaTrash} size={14} />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      {/* Modals */}
      {selectedCompany && showEditModal && (
        <CompanyFormModal
          show={showEditModal}
          onHide={handleCloseEditModal}
          company={selectedCompany}
        />
      )}
      {selectedCompany && showDetailModal && (
        <CompanyDetailModal
          show={showDetailModal}
          onHide={handleCloseDetailModal}
          company={selectedCompany}
        />
      )}
      {selectedCompany && showUsersModal && (
        <CompanyUsersModal
          show={showUsersModal}
          onHide={handleCloseUsersModal}
          company={selectedCompany}
        />
      )}
      {showClientCompaniesModal && selectedCompany && (
        <ClientCompaniesModal
          show={showClientCompaniesModal}
          onHide={handleCloseClientCompaniesModal}
          agencyCompany={selectedCompany}
        />
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        show={showDeleteModal}
        onCancel={() => {
          setShowDeleteModal(false);
          setSelectedCompany(null);
        }}
        onConfirm={handleConfirmDelete}
        title={t("delete_company")}
        message={`"${selectedCompany?.company_name}" ${t("company_delete_confirmation")}`}
      />

      <style>{`
        /* Table Wrapper */
        .companies-table-wrapper {
          background: ${
            isDark ? "rgba(30, 41, 59, 0.8)" : "rgba(255, 255, 255, 0.9)"
          };
          border-radius: 16px;
          box-shadow: ${
            isDark
              ? "0 8px 32px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.05)"
              : "0 8px 32px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.05)"
          };
          overflow: hidden;
          border: none;
          backdrop-filter: blur(20px);
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        /* Table */
        .companies-table {
          margin: 0;
          border-collapse: separate;
          border-spacing: 0;
          background: transparent;
        }

        .companies-table th,
        .companies-table td {
          border: none;
          border-bottom: 1px solid ${
            isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.05)"
          };
          vertical-align: middle;
        }

        /* Header */
        .table-header-cell {
          background: ${
            isDark ? "rgba(51, 65, 85, 0.5)" : "rgba(248, 250, 252, 0.8)"
          };
          padding: 20px 24px;
          font-weight: 600;
          color: ${isDark ? "#94a3b8" : "#6b7280"};
          font-size: 0.875rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          border-bottom: 2px solid ${
            isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)"
          };
          backdrop-filter: blur(10px);
        }

        .header-text {
          display: flex;
          align-items: center;
          font-weight: 600;
        }

        /* Table Rows */
        .table-row {
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          background: transparent;
        }

        .table-row:hover {
          background: ${
            isDark ? "rgba(51, 65, 85, 0.3)" : "rgba(248, 250, 252, 0.5)"
          };
          transform: translateY(-2px);
          box-shadow: ${
            isDark
              ? "0 8px 25px rgba(0, 0, 0, 0.3)"
              : "0 8px 25px rgba(0, 0, 0, 0.08)"
          };
        }

        .table-row:last-child td {
          border-bottom: none;
        }

        /* Table Cells */
        .table-cell {
          padding: 20px 24px;
          font-size: 0.875rem;
          color: ${isDark ? "#e2e8f0" : "#374151"};
        }

        /* Company Info */
        .company-info {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .company-name {
          font-weight: 600;
          color: ${isDark ? "#f1f5f9" : "#111827"};
          font-size: 0.95rem;
          line-height: 1.4;
        }

        .company-meta {
          display: flex;
          align-items: center;
          gap: 8px;
          color: ${isDark ? "#94a3b8" : "#6b7280"};
          font-size: 0.8rem;
        }

        .company-id {
          font-family: 'SF Mono', 'Monaco', monospace;
          font-weight: 600;
          color: #3b82f6;
          background: ${
            isDark ? "rgba(59, 130, 246, 0.15)" : "rgba(59, 130, 246, 0.1)"
          };
          padding: 4px 8px;
          border-radius: 6px;
          font-size: 0.75rem;
          border: 1px solid ${
            isDark ? "rgba(59, 130, 246, 0.3)" : "rgba(59, 130, 246, 0.2)"
          };
        }

        .separator {
          color: ${isDark ? "#64748b" : "#d1d5db"};
        }

        .website-icon {
          color: ${isDark ? "#94a3b8" : "#6b7280"};
        }

        .website-text {
          color: #3b82f6;
          font-weight: 500;
        }

        /* Type Badges */
        .type-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 8px 14px;
          border-radius: 20px;
          font-size: 0.8rem;
          font-weight: 500;
          text-transform: capitalize;
          backdrop-filter: blur(10px);
          transition: all 0.3s ease;
        }

        .type-agency {
          background: ${
            isDark ? "rgba(59, 130, 246, 0.2)" : "rgba(219, 234, 254, 0.9)"
          };
          color: ${isDark ? "#60a5fa" : "#1d4ed8"};
          border: 1px solid ${
            isDark ? "rgba(59, 130, 246, 0.3)" : "rgba(191, 219, 254, 0.8)"
          };
        }

        .type-client {
          background: ${
            isDark ? "rgba(34, 197, 94, 0.2)" : "rgba(220, 252, 231, 0.9)"
          };
          color: ${isDark ? "#4ade80" : "#166534"};
          border: 1px solid ${
            isDark ? "rgba(34, 197, 94, 0.3)" : "rgba(187, 247, 208, 0.8)"
          };
        }

        .type-default {
          background: ${
            isDark ? "rgba(71, 85, 105, 0.3)" : "rgba(243, 244, 246, 0.9)"
          };
          color: ${isDark ? "#cbd5e1" : "#374151"};
          border: 1px solid ${
            isDark ? "rgba(71, 85, 105, 0.4)" : "rgba(209, 213, 219, 0.8)"
          };
        }

        /* Industry Cell */
        .industry-cell {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .industry-icon {
          color: ${isDark ? "#94a3b8" : "#6b7280"};
          background: ${
            isDark ? "rgba(71, 85, 105, 0.3)" : "rgba(243, 244, 246, 0.8)"
          };
          padding: 6px;
          border-radius: 8px;
          width: 28px;
          height: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
          backdrop-filter: blur(10px);
        }

        .industry-text {
          color: ${isDark ? "#e2e8f0" : "#374151"};
          font-weight: 500;
        }

        /* Location Cell */
        .location-cell {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .location-icon {
          color: #ef4444;
          background: ${
            isDark ? "rgba(239, 68, 68, 0.15)" : "rgba(254, 242, 242, 0.8)"
          };
          padding: 6px;
          border-radius: 8px;
          width: 28px;
          height: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
          backdrop-filter: blur(10px);
        }

        .location-text {
          color: ${isDark ? "#e2e8f0" : "#374151"};
          font-weight: 500;
        }

        /* Status Badge */
        .status-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 8px 14px;
          border-radius: 20px;
          font-size: 0.8rem;
          font-weight: 500;
          backdrop-filter: blur(10px);
          transition: all 0.3s ease;
        }

        .status-active {
          background: ${
            isDark ? "rgba(34, 197, 94, 0.2)" : "rgba(220, 252, 231, 0.9)"
          };
          color: ${isDark ? "#4ade80" : "#166534"};
          border: 1px solid ${
            isDark ? "rgba(34, 197, 94, 0.3)" : "rgba(187, 247, 208, 0.8)"
          };
        }

        .status-inactive {
          background: ${
            isDark ? "rgba(239, 68, 68, 0.2)" : "rgba(254, 242, 242, 0.9)"
          };
          color: ${isDark ? "#f87171" : "#dc2626"};
          border: 1px solid ${
            isDark ? "rgba(239, 68, 68, 0.3)" : "rgba(254, 202, 202, 0.8)"
          };
        }

        .status-dot {
          animation: pulse 2s infinite;
        }

        .status-active .status-dot {
          color: ${isDark ? "#4ade80" : "#22c55e"};
        }

        .status-inactive .status-dot {
          color: ${isDark ? "#f87171" : "#ef4444"};
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }

        /* Date Text */
        .date-text {
          color: ${isDark ? "#94a3b8" : "#6b7280"};
          font-weight: 500;
          font-size: 0.85rem;
        }

        /* Action Buttons */
        .action-buttons {
          display: flex;
          gap: 8px;
          justify-content: flex-end;
        }

        .action-btn {
          border: 1px solid ${
            isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)"
          };
          background: ${
            isDark ? "rgba(51, 65, 85, 0.5)" : "rgba(255, 255, 255, 0.8)"
          };
          color: ${isDark ? "#cbd5e1" : "#6b7280"};
          padding: 10px 14px;
          border-radius: 10px;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          display: flex;
          align-items: center;
          justify-content: center;
          backdrop-filter: blur(10px);
        }

        .edit-btn:hover {
          background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
          border-color: transparent;
          color: white;
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(59, 130, 246, 0.35);
        }

        .delete-btn:hover {
          background: linear-gradient(135deg, #ef4444 0%, #f97316 100%);
          border-color: transparent;
          color: white;
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(239, 68, 68, 0.35);
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .table-cell {
            padding: 16px 12px;
          }

          .table-header-cell {
            padding: 16px 12px;
            font-size: 0.8rem;
          }

          .company-name {
            font-size: 0.9rem;
          }

          .company-meta {
            font-size: 0.75rem;
          }

          .action-buttons {
            flex-direction: column;
            gap: 6px;
          }

          .action-btn {
            padding: 8px 12px;
          }
        }

        /* Empty State */
        .companies-table tbody tr:only-child td {
          text-align: center;
          padding: 60px 20px;
          color: ${isDark ? "#94a3b8" : "#6b7280"};
          font-style: italic;
        }

        /* Focus states for accessibility */
        .action-btn:focus {
          outline: 2px solid ${isDark ? "#60a5fa" : "#3b82f6"};
          outline-offset: 2px;
        }

        /* Smooth transitions for all elements */
        * {
          transition: background-color 0.3s ease, color 0.3s ease, border-color 0.3s ease;
        }
          /* Style kısmına client-companies-btn için stil ekleyin */
.client-companies-btn:hover {
  background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%);
  border-color: transparent;
  color: white;
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(14, 165, 233, 0.35);
}
      `}</style>
    </>
  );
};

export default CompaniesList;
